import { setup, assign, fromPromise } from "xstate"
import type { FeedbackData, BugIssueData } from "@core/services/support.service"

// ================================
// TYPES
// ================================

export const SUPPORT_OPTIONS = {
    BUG: "BUG",
    FEEDBACK: "FEEDBACK",
    ISSUE: "ISSUE",
    DOC: "DOC",
} as const

export type SupportOptionValue =
    (typeof SUPPORT_OPTIONS)[keyof typeof SUPPORT_OPTIONS]

export interface SupportModalContext {
    title: string
    subtitle: string
    inputLevel: string
    inputMessage: string
    value: SupportOptionValue
    message: string
    error: string
    disabledButton: boolean
}

export interface SupportModalInput {
    onlyBug: boolean
    getTitle: (key: string) => string
    getCurrentPage: () => string
    getUsername: () => string
    formatDate: (date: Date) => string
    sendFeedback: (data: FeedbackData) => Promise<void>
    reportBugIssue: (data: BugIssueData) => Promise<void>
    onSuccess: (type: string) => void
}

export type SupportModalEvent =
    | { type: "OPEN" }
    | { type: "BUG" }
    | { type: "FEEDBACK" }
    | { type: "PREVIOUS" }
    | { type: "RESET" }
    | { type: "INPUT_LEVEL"; value: string }
    | { type: "INPUT_MESSAGE"; value: string }
    | { type: "SUBMIT" }

// ================================
// MACHINE
// ================================

/**
 * Machine à états simplifiée pour la modal support.
 * États : menu (accueil) → form (bug/feedback) → submitting (envoi).
 */
export function createSupportModalMachine(input: SupportModalInput) {
    return setup({
        types: {
            input: {} as SupportModalInput,
            context: {} as SupportModalContext,
            events: {} as SupportModalEvent,
        },
        guards: {
            onlyBug: () => input.onlyBug,
            canSubmit: ({ context }) =>
                context.value === SUPPORT_OPTIONS.BUG
                    ? context.inputMessage.trim() !== "" &&
                      context.inputLevel !== ""
                    : context.inputMessage.trim() !== "",
        },
        actions: {
            setMenu: assign({
                title: () => input.getTitle("heading1"),
                subtitle: () => "",
                value: () =>
                    (input.onlyBug
                        ? SUPPORT_OPTIONS.BUG
                        : ("" as SupportOptionValue)) as SupportOptionValue,
                inputLevel: () => "",
                inputMessage: () => "",
                message: () => "",
                error: () => "",
            }),
            setFormBug: assign({
                title: () => input.getTitle("heading_bug1"),
                subtitle: () => input.getTitle("heading_bug2"),
                value: () => SUPPORT_OPTIONS.BUG as SupportOptionValue,
                message: () => "",
                error: () => "",
            }),
            setFormFeedback: assign({
                title: () => input.getTitle("heading_feedback1"),
                subtitle: () => input.getTitle("heading_feedback2"),
                value: () => SUPPORT_OPTIONS.FEEDBACK as SupportOptionValue,
                message: () => "",
                error: () => "",
            }),
            setLevel: assign({
                inputLevel: ({ event }) =>
                    event.type === "INPUT_LEVEL" ? event.value : "",
            }),
            setMessage: assign({
                inputMessage: ({ event }) =>
                    event.type === "INPUT_MESSAGE" ? event.value : "",
            }),
            setValidationError: assign({
                message: () => "Veuillez renseigner les champs requis.",
            }),
            setApiError: assign({
                message: ({ event }) => {
                    const err = (
                        event as { error?: Error | { message?: string } }
                    ).error
                    if (!err) return "Une erreur est survenue"
                    return (
                        (typeof err === "object" && "message" in err
                            ? err.message
                            : String(err)) || "Une erreur est survenue"
                    )
                },
            }),
            clearMessage: assign({ message: () => "" }),
            onSuccess: ({ context }) => {
                input.onSuccess(context.value.toLowerCase())
            },
        },
        actors: {
            submitSupport: fromPromise(
                async ({
                    input: actorInput,
                }: {
                    input: SupportModalInput & {
                        context: SupportModalContext
                    }
                }) => {
                    const { context, ...inp } = actorInput
                    const dateStr = inp.formatDate(new Date())

                    if (context.value === SUPPORT_OPTIONS.FEEDBACK) {
                        await inp.sendFeedback({
                            subject: `Feedback utilisateur ${dateStr}`,
                            description: { text: context.inputMessage.trim() },
                        })
                        return {}
                    }

                    if (context.value === SUPPORT_OPTIONS.BUG) {
                        await inp.reportBugIssue({
                            subject: `Bug utilisateur ${dateStr}`,
                            description: {
                                level: context.inputLevel,
                                text: context.inputMessage.trim(),
                                currentPage: inp.getCurrentPage(),
                                username: inp.getUsername(),
                            },
                        })
                        return {}
                    }

                    return {}
                }
            ),
        },
    }).createMachine({
        id: "supportModal",
        initial: "menu",
        context: ({ input: inp }) => ({
            title: inp.getTitle("heading1"),
            subtitle: "",
            inputLevel: "",
            inputMessage: "",
            value: "" as SupportOptionValue,
            message: "",
            error: "",
            disabledButton: false,
        }),
        states: {
            menu: {
                on: {
                    OPEN: [
                        {
                            target: "form",
                            guard: "onlyBug",
                            actions: "setFormBug",
                        },
                        { target: "menu" },
                    ],
                    BUG: { target: "form", actions: "setFormBug" },
                    FEEDBACK: { target: "form", actions: "setFormFeedback" },
                    RESET: { actions: "setMenu" },
                },
            },
            form: {
                on: {
                    OPEN: [
                        {
                            target: "form",
                            guard: "onlyBug",
                            actions: "setFormBug",
                        },
                        { target: "menu", actions: "setMenu" },
                    ],
                    PREVIOUS: [
                        {
                            target: "form",
                            guard: ({ context }) =>
                                context.value === SUPPORT_OPTIONS.BUG,
                            actions: "setFormFeedback",
                        },
                        {
                            target: "form",
                            guard: ({ context }) =>
                                context.value === SUPPORT_OPTIONS.FEEDBACK,
                            actions: "setFormBug",
                        },
                    ],
                    RESET: { target: "menu", actions: "setMenu" },
                    INPUT_LEVEL: { actions: "setLevel" },
                    INPUT_MESSAGE: { actions: "setMessage" },
                    SUBMIT: [
                        {
                            target: "submitting",
                            guard: "canSubmit",
                        },
                        { target: "form", actions: "setValidationError" },
                    ],
                },
            },
            submitting: {
                entry: "clearMessage",
                invoke: {
                    id: "submitSupport",
                    src: "submitSupport",
                    input: ({ context }) => ({
                        ...input,
                        context,
                    }),
                    onDone: {
                        target: "menu",
                        actions: "onSuccess",
                    },
                    onError: {
                        target: "form",
                        actions: "setApiError",
                    },
                },
            },
        },
    })
}
